import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Info, Loader2 } from 'lucide-react';

const FOOTER_ACTION_BTN =
  'inline-flex h-10 min-w-[8.75rem] items-center justify-center rounded-md px-5 text-[14px] font-medium leading-none transition-colors box-border';

export const MOCK_AI_PRESUMED_VALUES = [
  { id: 1, label: 'Column size', value: '230 x 230mm' },
  { id: 2, label: 'Wall thickness', value: '9 inches' },
  { id: 3, label: 'Cement mortar ratio', value: '1:6' },
  { id: 4, label: 'Plaster thickness', value: '12mm' }
];

export const MOCK_QUESTION_SECTIONS = [
  {
    id: 'foundation',
    title: 'Foundation Detail',
    questions: [
      {
        id: 'foundation-type',
        label: 'What type of foundation?',
        options: ['PCC + brick footing', 'Strip footing', 'PCC + RCC footing', 'Isolated footing'],
        aiGenerated: true
      },
      {
        id: 'foundation-depth',
        label: 'Foundation depth below ground?',
        options: ['600mm', '900mm', '1200mm', 'Custom'],
        allowsCustomInput: true
      },
      {
        id: 'pcc-footing',
        label: 'PCC below footing — thickness and grade?',
        options: ['75mm · M10', '100mm · M10', '75mm · M15', 'No PCC'],
        allowsCustomInput: true
      }
    ]
  }
];

export const MOCK_TOTAL_QUESTIONS = 7;

const normalizeOptions = (options = []) => {
  if (!options.length) return [];
  if (typeof options[0] === 'string') {
    return options.map((label, index) => ({ id: `mock-${index}`, label }));
  }
  return options.map((opt) => ({
    id: opt.id,
    label: opt.label,
    value: opt.value
  }));
};

const getOptionLabels = (question) =>
  normalizeOptions(question.options).map((option) => option.label);

const isCustomAnswer = (answer, question) => {
  if (answer == null || String(answer).trim() === '') return false;
  return !getOptionLabels(question).includes(answer);
};

const QuestionsInfoBanner = ({ documentType }) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3.5 flex gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E0F2F4] text-[#2A6A73]">
      <Info size={18} strokeWidth={1.75} />
    </span>
    <p className="text-[14px] text-[#4B5563] leading-relaxed">
      You&apos;re answering questions for{' '}
      <span className="font-semibold text-[#111827]">{documentType}</span>. Choose a preset
      option or type your own answer when a text field is shown.
    </p>
  </div>
);

const OptionChip = ({ label, selected, disabled, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={disabled}
    className={`rounded-lg border px-3.5 py-2 text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      selected
        ? 'border-[#2A6A73] bg-[#E8F4F6] text-[#1F4E56]'
        : 'border-[#E5E7EB] bg-white text-[#374151] hover:border-[#C5CCD3] hover:bg-[#F9FAFB]'
    }`}>
    {label}
  </button>
);

const QuestionBlock = ({
  question,
  answer,
  customDraft,
  isSaving,
  onSelectOption,
  onCustomDraftChange,
  onSubmitCustom
}) => {
  const options = normalizeOptions(question.options);
  const showCustomInput = Boolean(question.allowsCustomInput);

  return (
    <div className="px-5 py-4 border-b border-[#F3F4F6] last:border-b-0">
      <div className="flex items-start justify-between gap-3 mb-1">
        <p className="text-[14px] font-semibold text-[#111827] leading-snug">{question.label}</p>
        {isSaving && (
          <Loader2 size={18} className="shrink-0 animate-spin text-[#2A6A73]" aria-hidden />
        )}
      </div>
      {question.reason && (
        <p className="mb-3 text-[12px] text-[#6B7280] leading-snug">{question.reason}</p>
      )}

      {options.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <OptionChip
              key={option.id}
              label={option.label}
              selected={answer === option.label}
              disabled={isSaving}
              onSelect={() => onSelectOption?.(question.id, option.id, option.label)}
            />
          ))}
        </div>
      )}

      {showCustomInput && (
        <div className={options.length > 0 ? 'mt-4' : ''}>
          <label
            htmlFor={`custom-${question.id}`}
            className="block text-[12px] font-medium text-[#6B7280] mb-1.5">
            Other / custom answer
          </label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              id={`custom-${question.id}`}
              type="text"
              value={customDraft}
              onChange={(e) => onCustomDraftChange?.(question.id, e.target.value)}
              disabled={isSaving}
              placeholder="Type your answer…"
              className="flex-1 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-[14px] text-[#111827] placeholder:text-[#9CA3AF] focus:border-[#2A6A73] focus:outline-none focus:ring-1 focus:ring-[#2A6A73] disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => onSubmitCustom?.(question.id, customDraft)}
              disabled={isSaving || !customDraft?.trim()}
              className="shrink-0 rounded-md border border-transparent bg-[#2A6A73] px-4 py-2.5 text-[13px] font-medium text-white hover:bg-[#245a62] disabled:opacity-50 disabled:cursor-not-allowed">
              Save answer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionSection = ({
  section,
  answers,
  customDrafts,
  savingQuestionId,
  onSelectOption,
  onCustomDraftChange,
  onSubmitCustom
}) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
    <div className="bg-[#E8F4F8] px-5 py-3 border-b border-[#D1E8ED]">
      <h3 className="text-[14px] font-bold text-[#1F4E56]">{section.title}</h3>
    </div>
    {section.questions.map((question) => (
      <QuestionBlock
        key={question.id}
        question={question}
        answer={answers[question.id]}
        customDraft={customDrafts[question.id] ?? ''}
        isSaving={savingQuestionId === question.id}
        onSelectOption={onSelectOption}
        onCustomDraftChange={onCustomDraftChange}
        onSubmitCustom={onSubmitCustom}
      />
    ))}
  </div>
);

const AiPresumedValuesPanel = ({ values = MOCK_AI_PRESUMED_VALUES, presumedCount }) => {
  const count = presumedCount ?? values.length;

  return (
    <aside className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)] lg:sticky lg:top-[10.5rem]">
      <div className="bg-[#E8F4F8] px-5 py-4 border-b border-[#D1E8ED]">
        <h3 className="text-[15px] font-bold text-[#111827] leading-tight">AI Presumed Values</h3>
        <p className="mt-1.5 text-[13px] text-[#6B7280] leading-snug">
          {count} {count === 1 ? 'value was' : 'values were'} presumed by AI using IS code standards
        </p>
      </div>
      <ul className="space-y-2.5 bg-white px-4 py-4">
        {values.map((item) => (
          <li
            key={item.id}
            className="flex items-start justify-between gap-4 rounded-xl bg-[#ECEEF1] px-4 py-2.5 min-h-[40px]">
            <span className="text-[13px] font-medium text-[#111827] min-w-0 flex-1 break-words leading-snug">
              {item.label}
            </span>
            <span className="text-[13px] font-semibold text-[#111827] shrink-0 text-right leading-snug">
              {item.value}
            </span>
          </li>
        ))}
      </ul>
    </aside>
  );
};

export const BoqBomQuestionsFooter = ({
  collapsed,
  answeredCount,
  totalQuestions,
  readyForBoqEngine = false,
  notesForNextStep,
  isGenerating = false,
  onPreviewBoq,
  onGenerateBoq,
  extractRequiredError,
  onUploadFiles
}) => (
  <div
    className={`fixed bottom-0 right-0 z-[5] bg-white border-t border-[#E5E7EB] px-6 md:px-8 py-4 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] transition-all duration-300 ease-in-out ${
      collapsed ? 'left-[76px]' : 'left-64'
    }`}>
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-[#111827]">
          {answeredCount} of {totalQuestions} questions answered
          {readyForBoqEngine && (
            <span className="ml-2 text-[#166534] font-medium">· Ready for BOQ</span>
          )}
        </p>
        <p className="mt-1 text-[13px] text-[#6B7280] leading-snug">
          {extractRequiredError ? (
            <span className="block text-[#B45309]">{extractRequiredError}</span>
          ) : readyForBoqEngine
            ? 'All requirements met. You can generate BOQ now.'
            : notesForNextStep ??
              'Answer every question on the server before BOQ generation is enabled.'}
        </p>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-3">
        {extractRequiredError ? (
          <button
            type="button"
            onClick={onUploadFiles}
            className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62]`}>
            Upload files
          </button>
        ) : (
          <>
        <button
          type="button"
          onClick={onPreviewBoq}
          className={`${FOOTER_ACTION_BTN} gap-2 border border-[#2A6A73] bg-white text-[#2A6A73] hover:bg-[#F4F9FA]`}>
          <Eye size={16} strokeWidth={1.75} />
          Preview BOQ
        </button>
        <button
          type="button"
          onClick={onGenerateBoq}
          disabled={!readyForBoqEngine || isGenerating}
          title={
            readyForBoqEngine
              ? 'Generate BOQ'
              : notesForNextStep ?? 'Complete all clarification questions first'
          }
          className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62] disabled:opacity-50 disabled:cursor-not-allowed`}>
          {isGenerating ? 'Generating…' : 'Generate BOQ'}
        </button>
          </>
        )}
      </div>
    </div>
  </div>
);

const buildInitialAnswers = (sections) => {
  const answers = {};
  sections.forEach((section) => {
    section.questions?.forEach((q) => {
      if (q.answer) answers[q.id] = q.answer;
    });
  });
  return answers;
};

const buildInitialCustomDrafts = (sections) => {
  const customDrafts = {};
  sections.forEach((section) => {
    section.questions?.forEach((q) => {
      if (q.answer && q.allowsCustomInput && isCustomAnswer(q.answer, q)) {
        customDrafts[q.id] = q.answer;
      }
    });
  });
  return customDrafts;
};

export const BoqBomQuestions = ({
  sections = MOCK_QUESTION_SECTIONS,
  totalQuestions = MOCK_TOTAL_QUESTIONS,
  documentType = 'Column / Pier Detail',
  presumedValues = MOCK_AI_PRESUMED_VALUES,
  presumedCount = 5,
  onSelectOption,
  onSubmitCustomAnswer,
  onProgressChange
}) => {
  const [answers, setAnswers] = useState(() => buildInitialAnswers(sections));
  const [customDrafts, setCustomDrafts] = useState(() => buildInitialCustomDrafts(sections));
  const [savingQuestionId, setSavingQuestionId] = useState(null);

  useEffect(() => {
    setAnswers(buildInitialAnswers(sections));
    setCustomDrafts(buildInitialCustomDrafts(sections));
  }, [sections]);

  const answeredCount = useMemo(
    () =>
      sections.reduce((count, section) => {
        const answeredInSection =
          section.questions?.filter((q) => {
            const value = answers[q.id];
            return value != null && String(value).trim() !== '';
          }).length ?? 0;
        return count + answeredInSection;
      }, 0),
    [answers, sections]
  );

  useEffect(() => {
    onProgressChange?.({ answeredCount, totalQuestions });
  }, [answeredCount, totalQuestions, onProgressChange]);

  const runSave = async (questionId, saveFn) => {
    setSavingQuestionId(questionId);
    try {
      await saveFn();
    } finally {
      setSavingQuestionId(null);
    }
  };

  const handleSelectOption = (questionId, optionId, label) => {
    if (!onSelectOption) {
      setAnswers((prev) => ({ ...prev, [questionId]: label }));
      return;
    }
    runSave(questionId, async () => {
      const updated = await onSelectOption(questionId, optionId, label);
      const savedAnswer = updated?.answer ?? label;
      setAnswers((prev) => ({ ...prev, [questionId]: savedAnswer }));
      setCustomDrafts((prev) => ({ ...prev, [questionId]: '' }));
    });
  };

  const handleCustomDraftChange = (questionId, value) => {
    setCustomDrafts((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmitCustom = (questionId, value) => {
    const trimmed = value?.trim();
    if (!trimmed) return;

    if (!onSubmitCustomAnswer) {
      setAnswers((prev) => ({ ...prev, [questionId]: trimmed }));
      return;
    }

    runSave(questionId, async () => {
      const updated = await onSubmitCustomAnswer(questionId, trimmed);
      const savedAnswer = updated?.answer ?? trimmed;
      setAnswers((prev) => ({ ...prev, [questionId]: savedAnswer }));
      setCustomDrafts((prev) => ({ ...prev, [questionId]: savedAnswer }));
    });
  };

  return (
    <div className="max-w-[1280px] mx-auto space-y-5">
      <QuestionsInfoBanner documentType={documentType} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
        <div className="space-y-5 min-w-0">
          {sections.map((section) => (
            <QuestionSection
              key={section.id}
              section={section}
              answers={answers}
              customDrafts={customDrafts}
              savingQuestionId={savingQuestionId}
              onSelectOption={handleSelectOption}
              onCustomDraftChange={handleCustomDraftChange}
              onSubmitCustom={handleSubmitCustom}
            />
          ))}
        </div>
        <AiPresumedValuesPanel values={presumedValues} presumedCount={presumedCount} />
      </div>
    </div>
  );
};
